/* @refresh reload */
import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo, ReactNode } from 'react';
import { useSocketContext } from '@swtorbit/shared-react-hooks';
import {
  SocketEvent,
  DiscordUser,
  DiscordGuild,
  DiscordChannel,
  DiscordMember,
  DiscordMembersListResponse,
  DiscordRole,
  DiscordRolesListResponse,
} from '@swtorbit/shared-socket-events';

interface DiscordContextType {
  // Auth state
  isLinked: boolean;
  discordUser: DiscordUser | null;
  
  // Guild state (list of available guilds from bot)
  guilds: DiscordGuild[];
  isLoadingGuilds: boolean;
  
  // Channel state
  channels: DiscordChannel[];
  isLoadingChannels: boolean;
  
  // Member state
  members: DiscordMember[];
  isLoadingMembers: boolean;

  // Role state
  roles: DiscordRole[];
  isLoadingRoles: boolean;

  // Actions
  fetchGuilds: (force?: boolean) => void;
  fetchChannelsAndMembers: (guildId: string) => void;
  selectGuild: (guildId: string, roomCode: string) => void;
  installBot: (guildId: string) => void;
  linkDiscord: () => void;
  setDiscordLinked: (isLinked: boolean, user: DiscordUser | null) => void;
  
  // Bot installation state
  isInstallingBot: string | null;
}

const DiscordContext = createContext<DiscordContextType | undefined>(undefined);

interface DiscordProviderProps {
  children: ReactNode;
  isAuthenticated: boolean;
}

export function DiscordProvider({ children, isAuthenticated }: DiscordProviderProps) {
  const { socket } = useSocketContext();
  
  // Auth state
  const [isLinked, setIsLinked] = useState(false);
  const [discordUser, setDiscordUser] = useState<DiscordUser | null>(null);
  
  // Guild state (available guilds from bot - not the selected guild, which is per-game)
  const [guilds, setGuilds] = useState<DiscordGuild[]>([]);
  const [isLoadingGuilds, setIsLoadingGuilds] = useState(false);
  
  // Channel state
  const [channels, setChannels] = useState<DiscordChannel[]>([]);
  const [isLoadingChannels, setIsLoadingChannels] = useState(false);
  
  // Member state
  const [members, setMembers] = useState<DiscordMember[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);

  // Role state
  const [roles, setRoles] = useState<DiscordRole[]>([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  
  // Bot installation state
  const [isInstallingBot, setIsInstallingBot] = useState<string | null>(null);
  
  // Caching refs - persist across component remounts
  const hasRequestedStatusRef = useRef(false);
  const hasFetchedGuildsRef = useRef(false);
  const fetchedMembersForGuildRef = useRef<string | null>(null);
  
  // Track popup poll interval for cleanup
  const popupPollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Request Discord status on mount (once per session)
  useEffect(() => {
    if (!socket || !isAuthenticated || hasRequestedStatusRef.current) return;
    hasRequestedStatusRef.current = true;
    socket.emit(SocketEvent.DISCORD_GET_STATUS);
  }, [socket, isAuthenticated]);

  // Socket event listeners
  useEffect(() => {
    if (!socket) return;

    // Handle Discord status response - only handles auth status
    // Guild selection is per-game and comes from the game's discord settings
    const handleStatus = (data: { 
      isLinked: boolean; 
      discordUser?: { id: string; username: string; avatar?: string }; 
      botConnected: boolean;
    }) => {
      if (data.isLinked && data.discordUser) {
        setIsLinked(true);
        setDiscordUser({
          id: data.discordUser.id,
          username: data.discordUser.username,
          avatar: data.discordUser.avatar,
        });
        
        // Always fetch guilds list so we can look up guild info for games
        if (!hasFetchedGuildsRef.current) {
          hasFetchedGuildsRef.current = true;
          socket.emit(SocketEvent.DISCORD_GET_GUILDS);
          setIsLoadingGuilds(true);
        }
      } else {
        setIsLinked(false);
        setDiscordUser(null);
        setIsLoadingGuilds(false);
      }
    };

    // Handle guilds response
    const handleGuilds = (data: { guilds: DiscordGuild[] }) => {
      setGuilds(data.guilds);
      setIsLoadingGuilds(false);
    };

    // Handle guild selection response
    // Note: the updated game (new guild + reset settings) arrives separately via
    // GAME_STATE_UPDATED; here we just fetch channels/members for the new guild
    const handleGuildSelected = (data: { success: boolean; guild?: DiscordGuild; error?: string }) => {
      if (data.success && data.guild) {
        // Fetch channels, members, and roles for the selected guild
        if (fetchedMembersForGuildRef.current !== data.guild.id) {
          fetchedMembersForGuildRef.current = data.guild.id;
          socket.emit(SocketEvent.DISCORD_GET_CHANNELS, { guildId: data.guild.id });
          socket.emit(SocketEvent.DISCORD_GET_MEMBERS, { guildId: data.guild.id });
          socket.emit(SocketEvent.DISCORD_GET_ROLES, { guildId: data.guild.id });
          setIsLoadingChannels(true);
          setIsLoadingMembers(true);
          setIsLoadingRoles(true);
        }
      }
    };

    // Handle channels response
    const handleChannels = (data: { channels: DiscordChannel[] }) => {
      setChannels(data.channels);
      setIsLoadingChannels(false);
    };

    // Handle members response
    const handleMembers = (data: DiscordMembersListResponse) => {
      if (data.success && data.members) {
        setMembers(data.members);
      }
      setIsLoadingMembers(false);
    };

    // Handle roles response
    const handleRoles = (data: DiscordRolesListResponse) => {
      if (data.success && data.roles) {
        setRoles(data.roles);
      }
      setIsLoadingRoles(false);
    };

    // Handle bot invite URL response
    const handleBotInvite = (data: { url: string; guildId?: string }) => {
      if (data.url) {
        const popup = window.open(data.url, 'discord-bot-install', 'width=500,height=800');
        if (popup) {
          // Clear any existing poll interval
          if (popupPollIntervalRef.current) {
            clearInterval(popupPollIntervalRef.current);
          }
          
          popupPollIntervalRef.current = setInterval(() => {
            if (popup.closed) {
              if (popupPollIntervalRef.current) {
                clearInterval(popupPollIntervalRef.current);
                popupPollIntervalRef.current = null;
              }
              setIsInstallingBot(null);
              // Refresh guilds list after bot installation
              hasFetchedGuildsRef.current = false;
              socket.emit(SocketEvent.DISCORD_GET_GUILDS);
              setIsLoadingGuilds(true);
            }
          }, 500);
        }
      }
    };

    socket.on(SocketEvent.DISCORD_STATUS, handleStatus);
    socket.on(SocketEvent.DISCORD_GUILDS_LIST, handleGuilds);
    socket.on(SocketEvent.DISCORD_GUILD_SELECTED, handleGuildSelected);
    socket.on(SocketEvent.DISCORD_CHANNELS_LIST, handleChannels);
    socket.on(SocketEvent.DISCORD_MEMBERS_LIST, handleMembers);
    socket.on(SocketEvent.DISCORD_ROLES_LIST, handleRoles);
    socket.on(SocketEvent.DISCORD_BOT_INVITE, handleBotInvite);

    return () => {
      socket.off(SocketEvent.DISCORD_STATUS, handleStatus);
      socket.off(SocketEvent.DISCORD_GUILDS_LIST, handleGuilds);
      socket.off(SocketEvent.DISCORD_GUILD_SELECTED, handleGuildSelected);
      socket.off(SocketEvent.DISCORD_CHANNELS_LIST, handleChannels);
      socket.off(SocketEvent.DISCORD_MEMBERS_LIST, handleMembers);
      socket.off(SocketEvent.DISCORD_ROLES_LIST, handleRoles);
      socket.off(SocketEvent.DISCORD_BOT_INVITE, handleBotInvite);
      
      // Clean up popup poll interval if component unmounts
      if (popupPollIntervalRef.current) {
        clearInterval(popupPollIntervalRef.current);
        popupPollIntervalRef.current = null;
      }
    };
  }, [socket]);

  // Fetch guilds (with optional force parameter to bypass cache)
  const fetchGuilds = useCallback((force = false) => {
    if (!socket) return;
    if (!force && hasFetchedGuildsRef.current) return;
    hasFetchedGuildsRef.current = true;
    socket.emit(SocketEvent.DISCORD_GET_GUILDS);
    setIsLoadingGuilds(true);
  }, [socket]);

  // Select a guild
  const selectGuild = useCallback((guildId: string, roomCode: string) => {
    if (!socket) return;
    socket.emit(SocketEvent.DISCORD_SELECT_GUILD, { guildId, roomCode });
  }, [socket]);

  // Install bot to a guild
  const installBot = useCallback((guildId: string) => {
    if (!socket) return;
    setIsInstallingBot(guildId);
    socket.emit(SocketEvent.DISCORD_GET_BOT_INVITE, { guildId });
  }, [socket]);

  // Start Discord OAuth flow
  const linkDiscord = useCallback(() => {
    if (!socket) return;
    socket.emit(SocketEvent.DISCORD_GET_AUTH_URL);
  }, [socket]);

  // Set Discord linked status (called after OAuth completes)
  const setDiscordLinked = useCallback((linked: boolean, user: DiscordUser | null) => {
    setIsLinked(linked);
    setDiscordUser(user);
    // After linking, fetch guilds
    if (linked && socket) {
      hasFetchedGuildsRef.current = true;
      socket.emit(SocketEvent.DISCORD_GET_GUILDS);
      setIsLoadingGuilds(true);
    }
  }, [socket]);

  // Fetch channels and members for a specific guild
  const fetchChannelsAndMembers = useCallback((guildId: string) => {
    if (!socket) return;
    if (fetchedMembersForGuildRef.current !== guildId) {
      fetchedMembersForGuildRef.current = guildId;
      socket.emit(SocketEvent.DISCORD_GET_CHANNELS, { guildId });
      socket.emit(SocketEvent.DISCORD_GET_MEMBERS, { guildId });
      socket.emit(SocketEvent.DISCORD_GET_ROLES, { guildId });
      setIsLoadingChannels(true);
      setIsLoadingMembers(true);
      setIsLoadingRoles(true);
    }
  }, [socket]);

  const value: DiscordContextType = useMemo(() => ({
    isLinked,
    discordUser,
    guilds,
    isLoadingGuilds,
    channels,
    isLoadingChannels,
    members,
    isLoadingMembers,
    roles,
    isLoadingRoles,
    fetchGuilds,
    fetchChannelsAndMembers,
    selectGuild,
    installBot,
    linkDiscord,
    setDiscordLinked,
    isInstallingBot,
  }), [
    isLinked,
    discordUser,
    guilds,
    isLoadingGuilds,
    channels,
    isLoadingChannels,
    members,
    isLoadingMembers,
    roles,
    isLoadingRoles,
    fetchGuilds,
    fetchChannelsAndMembers,
    selectGuild,
    installBot,
    linkDiscord,
    setDiscordLinked,
    isInstallingBot,
  ]);

  return (
    <DiscordContext.Provider value={value}>
      {children}
    </DiscordContext.Provider>
  );
}

export function useDiscordContext() {
  const context = useContext(DiscordContext);
  if (context === undefined) {
    throw new Error('useDiscordContext must be used within a DiscordProvider');
  }
  return context;
}
